import { OrderDetail } from '@interfaces/OrderTypes';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { generatePdf, generateDoublePdf } from '@utils/printUtils';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useDetailedOrders } from './orders';
import { usePrintServer } from './usePrintServer';

interface PrintOrderProps {
  orderId: string;
  detailedOrder?: OrderDetail;
}

export const usePrintOrder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { commerce } = useGetCommerce();
  const { getOrder } = useDetailedOrders();
  const { sendToPrintServer, isPrintServerConfigured, isLoading: isSendingToPrintServer } = usePrintServer();

  const printOrder = async ({ orderId, detailedOrder }: PrintOrderProps) => {
    try {
      setIsLoading(true);
      const response = detailedOrder || (await getOrder(orderId));

      // Enviar al servidor de impresión si está configurado (contexto: pedidos normales)
      let printedOnServer = false;
      if (isPrintServerConfigured('order')) {
        printedOnServer = await sendToPrintServer(response, 'order');
        if (printedOnServer) {
          toast.success('Enviado a impresora remota');
          return;
        }
      }

      // Fallback: Leer tipo de ticket desde localStorage (para impresión web local)
      const ticketType = (localStorage.getItem('printTicketType') as 'normal' | 'double') || 'normal';

      const pdf =
        ticketType === 'double'
          ? await generateDoublePdf(
              commerce,
              response.pedido,
              response.productos,
              commerce?.config_form_entrega?.referencias?.label || 'Referencias',
            )
          : await generatePdf(
              commerce,
              response.pedido,
              response.productos,
              commerce?.config_form_entrega?.referencias?.label || 'Referencias',
            );

      pdf.print();
    } catch {
      toast.error('Error al obtener los detalles del pedido o el pedido no existe');
    } finally {
      setIsLoading(false);
    }
  };

  return { printOrder, isLoading: isLoading || isSendingToPrintServer };
};
